import { FC } from 'react';
import { Box, Button, Chip, Grid, Stack, Typography } from '@mui/material';
import { UIFormLabel } from '../ui/ui-form-label';
import { numericFormatter } from 'react-number-format';

import { DealRequest } from '@/app/api/types';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { DealStatusChip } from '../deal-status-chip';
import { getDealSignatureStatusLabel } from '../single-deal/lib/get-single-deal-status';
import {
  getDealBuyerName,
  getInternationalDealKind,
  getInternationalDealLabel,
} from '@/app/helpers';

interface Props {
  data: DealRequest;
  to: number;
  type: 'seller' | 'buyer' | 'factor';
}

export const RequestItem: FC<Props> = ({ data, to, type }) => {
  const { t } = useTranslation();

  const internationalKind = getInternationalDealKind(data);
  const isClosedDeal = data.factoringType === 'closed';
  const isReverseDeal = data.factoringType === 'reverse';
  const isOpenedDeal = data.factoringType === 'opened';
  const isExportDeal = internationalKind === 'export';
  const isImportDeal = internationalKind === 'import';

  let customType = type;

  if (data.factoringType === 'reverse') {
    if (type === 'buyer') {
      customType = 'seller';
    } else {
      customType = 'buyer';
    }
  }

  const backUrl = encodeURIComponent(
    window.location.pathname + window.location.search
  );

  const href = `${to}?backUrl=${backUrl}`;

  return (
    <Box
      sx={{
        px: 2,
        py: 1.5,
        border: '1px solid rgba(112, 125, 159, 0.2)',
        borderRadius: '8px',
      }}
    >
      <Grid container spacing={2}>
        <Grid item xs={2}>
          <Typography color='primary.main' fontSize={14} fontWeight={600}>
            {t('bid')} №{data.number}{' '}
            <Box
              component='span'
              color='rgba(11, 19, 112, .5)'
              fontWeight={400}
            >
              {t('from_value', { date: data.createdAt }).toLowerCase()}
            </Box>
          </Typography>
        </Grid>
        <Grid item xs={10}>
          <Stack direction='row' spacing={1}>
            <DealStatusChip
              status={data.status}
              type={customType}
              label={getDealSignatureStatusLabel(data, customType, t)}
            />

            {!isExportDeal && !isImportDeal ? (
              <>
                {isReverseDeal && (
                  <Chip
                    label={t('factoring_types.reverse')}
                    color='secondary'
                    variant='filled'
                  />
                )}
                {isOpenedDeal && (
                  <Chip
                    label={t('factoring_types.opened')}
                    color='success'
                    variant='filled'
                  />
                )}
                {isClosedDeal && (
                  <Chip
                    label={t('factoring_types.closed')}
                    color='info'
                    variant='filled'
                  />
                )}
              </>
            ) : (
              <Chip
                label={getInternationalDealLabel(
                  data,
                  isExportDeal ? 'export' : 'import',
                  t
                )}
                sx={{
                  background: isExportDeal ? '#5D32BF33' : '#00337033',
                  color: isExportDeal ? '#6B30BF' : '#003370',
                }}
                variant='filled'
              />
            )}
          </Stack>
        </Grid>

        <Grid item xs={2}>
          <Box>
            <UIFormLabel
              sx={{
                color: 'rgba(11, 19, 112, .5)',
                fontWeight: 400,
              }}
            >
              {t('buyer')}:
            </UIFormLabel>

            <Typography
              fontSize={14}
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {getDealBuyerName(data)}
            </Typography>
          </Box>
        </Grid>

        <Grid item xs={2}>
          <Box>
            <UIFormLabel
              sx={{
                color: 'rgba(11, 19, 112, .5)',
                fontWeight: 400,
              }}
            >
              {t('createDealForm:term_of_payment_contract')}:
            </UIFormLabel>
            <Typography fontSize={14}>{data.contractPaymentDueDate}</Typography>
          </Box>
        </Grid>

        <Grid item xs={2}>
          <Box>
            <UIFormLabel
              sx={{
                color: 'rgba(11, 19, 112, .5)',
                fontWeight: 400,
              }}
            >
              {internationalKind
                ? t('createDealForm:invoice_sum')
                : t('createDealForm:invoice_based_sum')}
              :
            </UIFormLabel>
            <Typography fontSize={14} fontWeight={600}>
              {numericFormatter(data.contractAmount.toFixed(2), {
                thousandSeparator: ' ',
              })}
            </Typography>
          </Box>
        </Grid>

        <Grid item xs={2}>
          <Box>
            <UIFormLabel
              sx={{ color: 'rgba(11, 19, 112, .5)', fontWeight: 400 }}
            >
              {t('debt_sum')}:
            </UIFormLabel>
            <Typography fontSize={14} fontWeight={600}>
              {numericFormatter(data.buyerDebtAmount?.toFixed(2) || '0', {
                thousandSeparator: ' ',
              })}
            </Typography>
          </Box>
        </Grid>

        <Grid item xs={1}>
          <Box>
            <UIFormLabel
              sx={{ color: 'rgba(11, 19, 112, .5)', fontWeight: 400 }}
            >
              {t('currency')}:
            </UIFormLabel>
            <Typography fontSize={14} fontWeight={600}>
              {t(`currency_options.${data.currency}`)}
            </Typography>
          </Box>
        </Grid>

        <Grid item xs={2}>
          <Box>
            <UIFormLabel
              sx={{
                color: 'rgba(11, 19, 112, .5)',
                fontWeight: 400,
              }}
            >
              {t('branch')}:
            </UIFormLabel>
            <Typography fontSize={14} fontWeight={600}>
              {data?.factor?.branch?.name}
            </Typography>
          </Box>
        </Grid>

        <Grid item xs={1} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Link to={href}>
            <Button>{t('more_details')}</Button>
          </Link>
        </Grid>
      </Grid>
    </Box>
  );
};
